import { useState, useCallback } from 'react';
import { useUser } from '../context/UserContext';

/**
 * useCreateCustomChannelPointsReward
 * ----------------------
 * Create a new Twitch custom-reward and track its status.
 *
 * @param {object}   params
 * @returns {{
 *   createReward: (cfg: {
 *     title: string,
 *     cost: number,
 *     prompt?: string
 *   }) => Promise<void>,
 *   errorText: string,
 *   requiresPermission: boolean,
 *   isLoading: boolean
 * }}
 *
 * @example
 * const {
 *   createReward,
 *   errorText,
 *   requiresPermission,
 *   isLoading,
 * } = useCreateCustomChannelPointsReward();
 *
 * const handleSave = () =>
 *   createReward({ title, cost, prompt, cb });
 */
const useCreateCustomChannelPointsReward = () => {
    const [errorText, setErrorText] = useState('');
    const [requiresPermission, setRequiresPermission] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const authCtx = useUser();
    const apiUrl = process.env.REACT_APP_API_URL;

    /** POST a new reward and handle all error cases. */
    const createReward = useCallback(
        /**
         * @param {{title: string, cost: number, prompt?: string, cb: function}} cfg
         */
        async ({title = '', cost = 1, prompt = '', cb} = {}) => {
            try {
                setIsLoading(true);
                setErrorText('');

                const url = `${apiUrl}/user/createcustomreward?token=${authCtx.token}&type=other`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({title, cost, prompt})
                });

                if (response.ok) {
                    if (typeof(cb) === 'function') {
                        cb();
                    } else {
                        // Success → reload so the user immediately sees the new reward
                        window.location.reload();
                    }
                    return;
                }

                // — Error handling —
                const { error = 'Unknown error' } = await response.json();
                let extra = ' - ';
                switch (response.status) {
                case 400:
                    extra += 'Check that the title (1-45 chars, unique), cost (≥ 1) and prompt (< 200 chars) are valid.';
                    break;
                case 401:
                    extra += 'Ratbot lacks permission to create rewards for you.';
                    setRequiresPermission(true);
                    break;
                case 403:
                    extra += 'You must be a Twitch Partner or Affiliate to create custom app rewards.';
                    break;
                case 500:
                    extra += 'Twitch experienced an internal error. Please try again later.';
                    break;
                default:
                    extra += 'An unexpected error occurred.';
                }
                setErrorText(`${error}${extra}`);
            } catch (err) {
                console.error(err);
                setErrorText(err?.message ?? 'Unknown error');
            } finally {
                setIsLoading(false);
            }
        }, [authCtx, apiUrl]
    );

    return { createReward, errorText, requiresPermission, isLoading };
};

export default useCreateCustomChannelPointsReward;